import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronDown } from "lucide-react";
import { faEye, faLock, faArrowRightFromBracket, faUser, faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { useLayout } from "@/contexts/LayoutContext";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/shadcn/dropdown-menu";
import { HeaderTip } from "./HeaderTip";
import { popupCenter } from "@/utils/popup";
import { setLegacyShellOverride } from "@/utils/legacyShell";
import { useWhatsNew } from "./whats-new/WhatsNewContext";

export const UserMenu = () => {
  const { data } = useLayout();
  const { entries, hasUnseen, openWhatsNew } = useWhatsNew();
  const routes = data.appRoutes;
  const legacyShell = data.legacyShell;
  const showSettings = data.visibleMaintenanceAreas.includes("UserSettings");
  const avatarStyle = { backgroundColor: data.customerMode ? "#757575" : data.userAvatarColor };
  const avatarContent = data.customerMode ? <FontAwesomeIcon icon={faEye} size="sm" /> : data.userInitials;

  return (
    <DropdownMenu modal={false}>
      <HeaderTip label="Account menu">
      <DropdownMenuTrigger asChild>
        <button
          className="group flex items-center gap-2 h-9 pl-1 pr-1 xl:pr-2.5 text-white rounded-full hover:bg-white/10 data-[state=open]:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/60 shrink-0"
          aria-label="User menu"
        >
          <span
            className="relative flex items-center justify-center size-7 rounded-full font-semibold shrink-0 select-none"
            style={{ ...avatarStyle, fontSize: 12 }}
          >
            {avatarContent}
            {hasUnseen && (
              <span
                data-testid="whats-new-avatar-dot"
                aria-hidden
                className="whats-new-dot-animated absolute -top-1 -right-1 size-3 shrink-0 rounded-full border-2 border-(--layout-top-nav-bg) bg-(--layout-side-nav-active-bg)"
              />
            )}
          </span>
          <span className="hidden xl:block whitespace-nowrap text-sm">{data.username}</span>
          <ChevronDown className="hidden xl:block size-3.5 shrink-0 text-white/70" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      </HeaderTip>
      <DropdownMenuContent align="end" className="min-w-56">
        <div className="flex items-center gap-3 px-2 py-2">
          <span
            className="flex items-center justify-center size-9 rounded-full font-semibold text-white shrink-0 select-none"
            style={{ ...avatarStyle, fontSize: 13 }}
            aria-hidden="true"
          >
            {avatarContent}
          </span>
          <span className="min-w-0 [overflow-wrap:anywhere] text-sm font-medium">{data.username}</span>
        </div>        {showSettings && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => popupCenter(routes.userPreferences, "UserPreferences", 1024, 768)}>
              <FontAwesomeIcon icon={faUser} className="mr-2" />
              My Preferences
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={routes.accountSettings}>
                <FontAwesomeIcon icon={faLock} className="mr-2" />
                My Account
              </a>
            </DropdownMenuItem>
          </>
        )}
        {entries.length > 0 && (
          <>
            {!showSettings && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={openWhatsNew} data-testid="whats-new-menu-item">
              <FontAwesomeIcon icon={faBullhorn} className="mr-2" />
              What&apos;s new
              {hasUnseen && (
                <span data-testid="whats-new-item-dot" aria-hidden className="whats-new-dot-animated ml-2 size-2.5 shrink-0 rounded-full bg-(--layout-side-nav-active-bg)" />
              )}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        {legacyShell?.canToggle && (
          <>
            <DropdownMenuCheckboxItem
              checked={legacyShell.enabled}
              onCheckedChange={(checked) => {
                setLegacyShellOverride(checked === true);
                window.location.reload();
              }}
            >
              Legacy pages in shell (debug)
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem asChild >
          <a href={routes.logout}>
            <FontAwesomeIcon icon={faArrowRightFromBracket} className="mr-2" />
            Logout
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
